/**
 * A vertical rail of steps: icon in a ring, connector to the next, label, and cost on the
 * right. Callers keep their vocabulary through `icon` and `tone`; the rail owns the geometry.
 */

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface RailStep {
  key: string;
  icon: LucideIcon;
  /** Colour for the icon. The rail never picks one: what a step means is the caller's. */
  tone?: string;
  label: ReactNode;
  /** Still going: the ring pulses and a spinner replaces the icon. */
  running?: boolean;
  /** Right-aligned, monospaced figure: what the step cost. */
  meta?: ReactNode;
  /** Sub-lines under the label: arguments, a result summary, an I/O pane. */
  detail?: ReactNode;
}

export const StepRail = ({ steps, className }: { steps: RailStep[]; className?: string }) => (
  <ol className={cn('flex flex-col', className)}>
    {steps.map((step, index) => {
      const last = index === steps.length - 1;
      const Icon = step.icon;
      return (
        <li
          key={step.key}
          className='flex items-start gap-2.5 py-1 animate-in fade-in-0 slide-in-from-bottom-1 duration-300 ease-out'
        >
          <div className='flex flex-col items-center self-stretch'>
            <span
              className={cn(
                'flex size-5 items-center justify-center rounded-full border border-gray-500 bg-white',
                step.running && 'animate-pulse'
              )}
            >
              {step.running ? (
                <Loader2 className='size-3 animate-spin text-indigo-500' aria-hidden />
              ) : (
                <Icon className={cn('size-3', step.tone ?? 'text-gray-800')} aria-hidden />
              )}
            </span>
            {!last && <span className='my-0.5 min-h-2.5 w-px flex-1 bg-gray-500' />}
          </div>
          <div className='min-w-0 flex-1 pt-0.5'>
            <div className='text-xs leading-snug text-text-body'>{step.label}</div>
            {step.detail}
          </div>
          {step.meta && <span className='shrink-0 pt-0.5 text-[10px] text-gray-800 tabular-nums'>{step.meta}</span>}
        </li>
      );
    })}
  </ol>
);
